import { supabase } from "../lib/supabase";
import { advisorService } from "./advisorService";

export const dashboardService = {
  // ✅ ALIAS para que NO reviente el dashboard:
  // Si tu dashboard llama getAdvisorStats(), aquí lo soportamos.
  async getAdvisorStats(advisorId) {
    // Reusa tu lógica actual
    const metrics = await this.getAdvisorMetrics(advisorId);

    // Devuelve en el formato “stats” típico que muchos dashboards esperan
    return {
      totalReferrals: metrics?.activeReferrals || 0,
      activeReferrals: metrics?.activeReferrals || 0,
      totalLeads: metrics?.leadsConverted || 0,
      activeLeads: metrics?.leadsConverted || 0,
      monthlyEarnings: Number(metrics?.totalEarnings || 0),
      totalEarnings: Number(metrics?.totalEarnings || 0),
      pointsAccumulated: metrics?.pointsAccumulated || 0,
      updatedAt: new Date().toISOString(),
    };
  },
  // ✅ ALIAS: tu dashboard lo llama desde dashboardService, pero vive en advisorService
  async getCommissionsByLevel(advisorId) {
    return await advisorService.getCommissionsByLevel(advisorId);
  },
  async getEarningsHistory(advisorId, months = 6) {
    return await advisorService.getEarningsHistory(advisorId, months);
  },

  async getActivityFeed(advisorId, limit = 10) {
    return await advisorService.getActivityFeed(advisorId, limit);
  },

  async getAvailableRewards() {
    return await advisorService.getAvailableRewards();
  },

  async claimReward(advisorId, rewardId) {
    return await advisorService.claimReward(advisorId, rewardId);
  },

  // Get advisor metrics
  async getAdvisorMetrics(advisorId) {
    try {
      const currentMonth = new Date();
      currentMonth.setDate(1);
      currentMonth.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from("advisor_metrics")
        .select("*")
        .eq("advisor_id", advisorId)
        .gte("month", currentMonth.toISOString())
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (!data) {
        return {
          totalEarnings: 0,
          activeReferrals: 0,
          pointsAccumulated: 0,
          leadsConverted: 0,
        };
      }

      return {
        totalEarnings: data.total_earnings,
        activeReferrals: data.active_referrals,
        pointsAccumulated: data.points_accumulated,
        leadsConverted: data.leads_converted,
      };
    } catch (error) {
      console.error("Error fetching advisor metrics:", error);
      // En vez de throw, devolvemos defaults para que el dashboard no explote
      return {
        totalEarnings: 0,
        activeReferrals: 0,
        pointsAccumulated: 0,
        leadsConverted: 0,
      };
    }
  },

  // Get referral network
  async getReferralNetwork(advisorId) {
    try {
      const { data, error } = await supabase
        .from("referral_network")
        .select(
          `
          *,
          referred:user_profiles!referral_network_referred_id_fkey(
            id,
            full_name,
            email,
            role
          )
        `
        )
        .eq("referrer_id", advisorId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data || []).map((ref) => ({
        id: ref.id,
        referrerId: ref.referrer_id,
        referredId: ref.referred_id,
        level: ref.level,
        commissionRate: ref.commission_rate,
        totalCommission: ref.total_commission,
        createdAt: ref.created_at,
        referred: {
          id: ref.referred?.id,
          fullName: ref.referred?.full_name,
          email: ref.referred?.email,
          role: ref.referred?.role,
        },
      }));
    } catch (error) {
      console.error("Error fetching referral network:", error);
      return [];
    }
  },

  // Get referral link
  async getReferralLink(userId) {
    try {
      const { data, error } = await supabase
        .from("referral_links")
        .select("*")
        .eq("user_id", userId)
        .single();

      if (error) throw error;

      return {
        id: data.id,
        userId: data.user_id,
        referralCode: data.referral_code,
        totalClicks: data.total_clicks,
        totalConversions: data.total_conversions,
        createdAt: data.created_at,
      };
    } catch (error) {
      console.error("Error fetching referral link:", error);
      return null;
    }
  },

  // Get recent activity (from CRM leads)
  async getRecentActivity(advisorId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from("crm_leads")
        .select("*")
        .eq("advisor_id", advisorId)
        .order("ultima_actividad", { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((lead) => ({
        id: lead.id,
        nombre: lead.nombre,
        stage: lead.stage,
        ultimaActividad: lead.ultima_actividad,
        nivelInteres: lead.nivel_interes,
      }));
    } catch (error) {
      console.error("Error fetching recent activity:", error);
      return [];
    }
  },

  // Get admin dashboard metrics
  async getAdminMetrics() {
    try {
      const [advisorsResult, leadsResult, salesResult, activeResult] =
        await Promise.all([
          supabase
            .from("user_profiles")
            .select("*", { count: "exact" })
            .eq("role", "advisor"),
          supabase.from("crm_leads").select("*", { count: "exact" }),
          supabase.from("advisor_metrics").select("total_earnings"),
          supabase
            .from("user_profiles")
            .select("*", { count: "exact" })
            .eq("role", "advisor")
            .eq("is_active", true),
        ]);

      const totalSales =
        salesResult?.data?.reduce(
          (sum, metric) => sum + parseFloat(metric?.total_earnings || 0),
          0
        ) || 0;

      return {
        totalAdvisors: advisorsResult?.count || 0,
        totalLeads: leadsResult?.count || 0,
        monthlySales: totalSales,
        activeUsers: activeResult?.count || 0,
      };
    } catch (error) {
      console.error("Error fetching admin metrics:", error);
      return {
        totalAdvisors: 0,
        totalLeads: 0,
        monthlySales: 0,
        activeUsers: 0,
      };
    }
  },

  // Get all floor plans for admin
  async getAllFloorPlans() {
    try {
      const { data, error } = await supabase?.from('floor_plans')?.select('*')?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(plan => ({
        id: plan?.id,
        projectName: plan?.project_name,
        sections: plan?.sections,
        lots: plan?.lots,
        pdfUrl: plan?.pdf_url,
        confidenceScore: plan?.confidence_score,
        createdAt: plan?.created_at,
        updatedAt: plan?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching floor plans:', error);
      return [];
    }
  },

  // Get pricing tiers for a floor plan
  async getPricingTiers(floorPlanId) {
    try {
      const { data, error } = await supabase?.from('pricing_tiers')?.select('*')?.eq('floor_plan_id', floorPlanId)?.order('lot_number', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(tier => ({
        id: tier?.id,
        lotNumber: tier?.lot_number,
        lotSizeSqm: parseFloat(tier?.lot_size_sqm || 0),
        basePrice: parseFloat(tier?.base_price || 0),
        discountPercentage: parseFloat(tier?.discount_percentage || 0),
        finalPrice: parseFloat(tier?.final_price || 0),
        separationAmount: parseFloat(tier?.separation_amount || 300),
        financingType: tier?.financing_type || 'direct',
        maxFinancingMonths: tier?.max_financing_months || 60,
        status: tier?.status,
        createdAt: tier?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching pricing tiers:', error);
      return [];
    }
  },

  // Get lot details with financing calculations
  async getLotDetailsWithFinancing(lotId) {
    try {
      const { data, error } = await supabase
        ?.from('lot_details_with_financing')
        ?.select('*')
        ?.eq('id', lotId)
        ?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        projectName: data?.project_name,
        lotNumber: data?.lot_number,
        lotSizeSqm: parseFloat(data?.lot_size_sqm || 0),
        basePrice: parseFloat(data?.base_price || 0),
        discountPercentage: parseFloat(data?.discount_percentage || 0),
        finalPrice: parseFloat(data?.final_price || 0),
        separationAmount: parseFloat(data?.separation_amount || 300),
        financingType: data?.financing_type,
        maxFinancingMonths: data?.max_financing_months || 60,
        status: data?.status,
        // 20% down payment option
        downPayment20: parseFloat(data?.down_payment_20 || 0),
        monthlyPayment20: parseFloat(data?.monthly_payment_20 || 0),
        totalInterest20: parseFloat(data?.total_interest_20 || 0),
        // 30% down payment option
        downPayment30: parseFloat(data?.down_payment_30 || 0),
        monthlyPayment30: parseFloat(data?.monthly_payment_30 || 0),
        totalInterest30: parseFloat(data?.total_interest_30 || 0)
      };
    } catch (error) {
      console.error('Error fetching lot details with financing:', error);
      return null;
    }
  },

  // Calculate financing for any amount
  async calculateFinancing(basePrice, downPaymentPercentage = 20, months = 60, annualRate = 8.5) {
    try {
      const { data, error } = await supabase
        ?.rpc('calculate_financing', {
          base_price: basePrice,
          down_payment_percentage: downPaymentPercentage,
          months: months,
          annual_rate: annualRate
        });
      
      if (error) throw error;
      
      return {
        downPayment: parseFloat(data?.[0]?.down_payment || 0),
        financedAmount: parseFloat(data?.[0]?.financed_amount || 0),
        monthlyPayment: parseFloat(data?.[0]?.monthly_payment || 0),
        totalInterest: parseFloat(data?.[0]?.total_interest || 0),
        totalPayment: parseFloat(data?.[0]?.total_payment || 0)
      };
    } catch (error) {
      console.error('Error calculating financing:', error);
      return null;
    }
  },

  // Calculate commission for a sale
  async calculateCommission(saleAmount, referralLevel = 1) {
    try {
      const { data, error } = await supabase
        ?.rpc('calculate_commission', {
          sale_amount: saleAmount,
          referral_level: referralLevel
        });
      
      if (error) throw error;
      
      return parseFloat(data || 0);
    } catch (error) {
      console.error('Error calculating commission:', error);
      return 0;
    }
  },

  // Get commission ranges
  async getCommissionRanges() {
    try {
      const { data, error } = await supabase?.from('commission_ranges')?.select('*')?.eq('is_active', true)?.order('level', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(range => ({
        id: range?.id,
        rangeName: range?.range_name,
        minSales: range?.min_sales,
        maxSales: range?.max_sales,
        commissionPercentage: parseFloat(range?.commission_percentage || 0),
        bonusAmount: parseFloat(range?.bonus_amount || 0),
        level: range?.level
      })) || [];
    } catch (error) {
      console.error('Error fetching commission ranges:', error);
      return [];
    }
  },

  // Create or update pricing tier
  async upsertPricingTier(tierData) {
    try {
      const dbData = {
        floor_plan_id: tierData?.floorPlanId,
        lot_number: tierData?.lotNumber,
        base_price: tierData?.basePrice,
        discount_percentage: tierData?.discountPercentage || 0,
        status: tierData?.status || 'available'
      };

      if (tierData?.id) {
        const { data, error } = await supabase?.from('pricing_tiers')?.update(dbData)?.eq('id', tierData?.id)?.select()?.single();
        if (error) throw error;
        return { data, error: null };
      } else {
        const { data, error } = await supabase?.from('pricing_tiers')?.insert(dbData)?.select()?.single();
        if (error) throw error;
        return { data, error: null };
      }
    } catch (error) {
      return { data: null, error };
    }
  },

  // Delete pricing tier
  async deletePricingTier(tierId) {
    try {
      const { error } = await supabase?.from('pricing_tiers')?.delete()?.eq('id', tierId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Export dashboard data to CSV
  async exportDashboardData(advisorId, dataType = 'all') {
    try {
      const data = {};
      
      if (dataType === 'all' || dataType === 'metrics') {
        const metrics = await this.getAdvisorMetrics(advisorId);
        data.metrics = metrics;
      }
      
      if (dataType === 'all' || dataType === 'referrals') {
        const referrals = await this.getReferralNetwork(advisorId);
        data.referrals = referrals;
      }
      
      if (dataType === 'all' || dataType === 'activity') {
        const activity = await this.getRecentActivity(advisorId, 100);
        data.activity = activity;
      }
      
      return data;
    } catch (error) {
      console.error('Error exporting dashboard data:', error);
      return null;
    }
  },

  // Get filtered leads for admin
  async getFilteredLeads(filters = {}) {
    try {
      let query = supabase?.from('crm_leads')?.select(`
        *,
        advisor:user_profiles!crm_leads_advisor_id_fkey(id, full_name, email)
      `);
      
      if (filters?.stage) {
        query = query?.eq('stage', filters?.stage);
      }
      
      if (filters?.advisorId) {
        query = query?.eq('advisor_id', filters?.advisorId);
      }
      
      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }
      
      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }
      
      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(lead => ({
        id: lead?.id,
        nombre: lead?.nombre,
        email: lead?.email,
        telefono: lead?.telefono,
        stage: lead?.stage,
        nivelInteres: lead?.nivel_interes,
        presupuesto: parseFloat(lead?.presupuesto || 0),
        advisor: lead?.advisor,
        createdAt: lead?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching filtered leads:', error);
      return [];
    }
  }
};

export default dashboardService;